import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, Button, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Card, CardContent, Grid2 as Grid, Alert, Divider, Tabs, Tab,
} from '@mui/material';
import {
  Refresh, Verified, Edit, Badge as BadgeIcon, CreditCard, Watch, Search,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Gestão do serviço INPS.
 *
 * Existe porque faltava o terceiro lado. O contribuinte declara o número no
 * perfil, o INPS confirma-o pela sua chave — e quando alguém declarava o número
 * errado, ou precisava de mudar um já confirmado, os endpoints respondiam
 * «contacte o suporte» e o suporte não tinha por onde o fazer.
 *
 * Confirmar e corrigir exigem motivo escrito, de propósito: confirmar um número
 * é o acto que o habilita a mover dinheiro, e sem motivo registado não há como
 * distinguir uma confirmação legítima de um favor.
 */

interface Contribuinte {
  id: number;
  numero_social: string;
  tipo: 'PESSOA' | 'EMPRESA';
  nome: string | null;
  estado: string;
  verificado: 'VERIFICADO' | 'NAO_VERIFICADO';
  verificado_em: string | null;
  declarado_por: string | null;
  email_titular: string | null;
  wallet_id: string | null;
  cartoes_activos: number;
  created_at: string;
}

interface Cartao {
  id: number;
  nfc_uid: string;
  suporte: 'CARTAO' | 'PULSEIRA';
  tecnologia: string;
  ambito: string;
  estado: 'ACTIVO' | 'BLOQUEADO' | 'PERDIDO' | 'SUBSTITUIDO';
  motivo: string | null;
  emitido_em: string | null;
  bloqueado_em: string | null;
}

interface Pagamento {
  id: string;
  referencia_divida: string;
  valor_cve: number;
  estado: string;
  terminal_id: string | null;
  created_at: string;
}

interface LinhaLeitura {
  origem: string;
  resultado: string;
  terminal_verificado: 'SIM' | 'NAO' | 'SEM_TERMINAL';
  total: number;
  latencia_media_ms: number | null;
  latencia_max_ms: number | null;
  primeira_leitura: string | null;
  ultima_leitura: string | null;
}

interface LeituraIndividual {
  nfc_uid: string;
  numero_social: string | null;
  origem: string;
  terminal_id: string | null;
  terminal_verificado: 'SIM' | 'NAO' | 'SEM_TERMINAL';
  resultado: string;
  latencia_ms: number | null;
  ip: string | null;
  created_at: string;
}

const nomeEquipamento = (o: string) =>
  o === 'COMPONENTE_5' ? 'Componente 5' : o === 'TERMINAL' ? 'Leitor do terminal' : 'Não declarado';

/** Data e hora ao segundo — nesta tabela interessa saber QUANDO, e ao minuto
 *  duas leituras seguidas confundem-se. */
const dataHora = (v?: string | null) =>
  v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }) : '—';

const chipVerificado = (v: string) => (
  <Chip size="small"
    label={v === 'SIM' ? 'Verificado' : v === 'NAO' ? 'Não confere' : 'Sem terminal'}
    color={v === 'SIM' ? 'success' : v === 'NAO' ? 'error' : 'default'}
    variant={v === 'SIM' ? 'filled' : 'outlined'} />
);

const dataCurta = (v?: string | null) =>
  v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', dateStyle: 'short', timeStyle: 'short' }) : '—';

const corDoEstado = (estado: string) =>
  estado === 'ACTIVO' ? 'success' : estado === 'PERDIDO' ? 'error' : 'default';

const INPSAdmin: React.FC = () => {
  const [aba, setAba] = useState(0);

  const [contribuintes, setContribuintes] = useState<Contribuinte[]>([]);
  const [procura, setProcura] = useState('');
  const [filtroVerificacao, setFiltroVerificacao] = useState('');
  const [aCarregar, setACarregar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [detalhe, setDetalhe] = useState<{ contribuinte: Contribuinte; cartoes: Cartao[]; pagamentos: Pagamento[] } | null>(null);
  const [dialogoAberto, setDialogoAberto] = useState(false);

  const [motivo, setMotivo] = useState('');
  const [novoNumero, setNovoNumero] = useState('');
  const [aGravar, setAGravar] = useState(false);

  const [leituras, setLeituras] = useState<LinhaLeitura[]>([]);
  const [ultimasLeituras, setUltimasLeituras] = useState<LeituraIndividual[]>([]);

  const carregar = useCallback(async () => {
    setACarregar(true);
    setErro(null);
    try {
      const { data } = await api.get('/admin/inps/contribuintes', {
        params: { procura: procura || undefined, verificado: filtroVerificacao || undefined },
      });
      setContribuintes(data.data || []);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível carregar os contribuintes.');
    } finally {
      setACarregar(false);
    }
  }, [procura, filtroVerificacao]);

  const carregarLeituras = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/inps/leituras', { params: { dias: 7 } });
      setLeituras(data.data?.porOrigem || []);
      setUltimasLeituras(data.data?.ultimas || []);
    } catch {
      setLeituras([]);
      setUltimasLeituras([]);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (aba === 1) carregarLeituras(); }, [aba, carregarLeituras]);

  const abrirDetalhe = async (id: number) => {
    setErro(null);
    try {
      const { data } = await api.get(`/admin/inps/contribuintes/${id}`);
      setDetalhe(data.data);
      setNovoNumero(data.data.contribuinte.numero_social);
      setMotivo('');
      setDialogoAberto(true);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível abrir o contribuinte.');
    }
  };

  const confirmar = async () => {
    if (!detalhe) return;
    setAGravar(true);
    try {
      await api.post(`/admin/inps/contribuintes/${detalhe.contribuinte.id}/verificar`, { motivo });
      await abrirDetalhe(detalhe.contribuinte.id);
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível confirmar.');
    } finally {
      setAGravar(false);
    }
  };

  const corrigirNumero = async () => {
    if (!detalhe) return;
    setAGravar(true);
    try {
      await api.put(`/admin/inps/contribuintes/${detalhe.contribuinte.id}/numero`, {
        numero_social: novoNumero, motivo,
      });
      await abrirDetalhe(detalhe.contribuinte.id);
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível corrigir o número.');
    } finally {
      setAGravar(false);
    }
  };

  const mudarEstadoCartao = async (cartaoId: number, estado: string) => {
    if (!detalhe) return;
    try {
      await api.post(`/admin/inps/cartoes/${cartaoId}/estado`, {
        estado,
        motivo: estado === 'ACTIVO' ? '' : (motivo || 'Alteração pelo suporte'),
      });
      await abrirDetalhe(detalhe.contribuinte.id);
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível alterar o suporte.');
    }
  };

  const c = detalhe?.contribuinte;
  const jaConfirmado = c?.verificado === 'VERIFICADO';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>INPS — Contribuintes</Typography>
        <Button startIcon={<Refresh />} onClick={() => { carregar(); carregarLeituras(); }}>
          Actualizar
        </Button>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ mb: 2 }}>
        <Tab label="Contribuintes" />
        <Tab label="Leituras por equipamento" />
      </Tabs>

      {aba === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth size="small" label="Procurar por número, nome ou email"
                  value={procura} onChange={(e) => setProcura(e.target.value)}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.disabled' }} /> }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth size="small" select label="Confirmação"
                  value={filtroVerificacao} onChange={(e) => setFiltroVerificacao(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="VERIFICADO">Confirmados</MenuItem>
                  <MenuItem value="NAO_VERIFICADO">Por confirmar</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nº contribuinte</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Titular</TableCell>
                  <TableCell>Confirmação</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Suportes</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aCarregar && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                )}
                {!aCarregar && contribuintes.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum contribuinte encontrado.
                  </TableCell></TableRow>
                )}
                {contribuintes.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.numero_social}</TableCell>
                    <TableCell>{row.tipo === 'EMPRESA' ? 'Empresa' : 'Pessoa'}</TableCell>
                    <TableCell>{row.email_titular || <em style={{ color: '#999' }}>sem titular</em>}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.verificado === 'VERIFICADO' ? 'Confirmado' : 'Por confirmar'}
                        color={row.verificado === 'VERIFICADO' ? 'success' : 'warning'}
                        variant={row.verificado === 'VERIFICADO' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.declarado_por === 'UTILIZADOR' ? 'declarado no perfil'
                          : row.declarado_por === 'INPS' ? 'criado pelo INPS'
                          : row.declarado_por === 'PAGALI' ? 'criado pela Pagali' : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.cartoes_activos}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => abrirDetalhe(row.id)}>Abrir</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {aba === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Últimos 7 dias. É esta a tabela que responde à pergunta do piloto: o componente 5 lê tão
            bem como o leitor acoplado ao terminal? Só as linhas <strong>Verificado</strong> contam para
            a comparação — nas outras, o equipamento é apenas o que o pedido declarou.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Equipamento</TableCell>
                  <TableCell>Verificado</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell align="right">Leituras</TableCell>
                  <TableCell align="right">Latência média</TableCell>
                  <TableCell align="right">Pior caso</TableCell>
                  <TableCell>Primeira leitura</TableCell>
                  <TableCell>Última leitura</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leituras.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Ainda não há leituras registadas.
                  </TableCell></TableRow>
                )}
                {leituras.map((l, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{nomeEquipamento(l.origem)}</TableCell>
                    <TableCell>{chipVerificado(l.terminal_verificado)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={l.resultado}
                        color={l.resultado === 'OK' ? 'success' : l.resultado === 'DESCONHECIDO' ? 'default' : 'error'} />
                    </TableCell>
                    <TableCell align="right">{l.total}</TableCell>
                    <TableCell align="right">{l.latencia_media_ms ?? '—'} ms</TableCell>
                    <TableCell align="right">{l.latencia_max_ms ?? '—'} ms</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataHora(l.primeira_leitura)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataHora(l.ultima_leitura)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            Leituras individuais
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cada leitura com a hora exacta, o terminal e o endereço de onde veio — é o que permite
            distinguir um equipamento no terreno de alguém a exercitar a API. Mostra as 100 mais recentes.
          </Typography>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Data e hora</TableCell>
                  <TableCell>Equipamento</TableCell>
                  <TableCell>Terminal</TableCell>
                  <TableCell>Verificado</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell>Cartão</TableCell>
                  <TableCell align="right">Latência</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ultimasLeituras.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Ainda não há leituras registadas.
                  </TableCell></TableRow>
                )}
                {ultimasLeituras.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{dataHora(r.created_at)}</TableCell>
                    <TableCell>{nomeEquipamento(r.origem)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.terminal_id || '—'}</TableCell>
                    <TableCell>{chipVerificado(r.terminal_verificado)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.resultado}
                        color={r.resultado === 'OK' ? 'success' : r.resultado === 'DESCONHECIDO' ? 'default' : 'error'} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.nfc_uid}</TableCell>
                    <TableCell align="right">{r.latencia_ms ?? '—'} ms</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.ip || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogoAberto} onClose={() => setDialogoAberto(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon color="primary" /> Contribuinte {c?.numero_social}
        </DialogTitle>
        <DialogContent dividers>
          {!detalhe ? <CircularProgress /> : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined"><CardContent>
                    <Typography variant="caption" color="text.secondary">Titular</Typography>
                    <Typography>{c?.email_titular || '— sem titular —'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Carteira</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c?.wallet_id || '—'}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined"><CardContent>
                    <Typography variant="caption" color="text.secondary">Confirmação</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip size="small" label={jaConfirmado ? 'Confirmado' : 'Por confirmar'}
                        color={jaConfirmado ? 'success' : 'warning'} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {jaConfirmado ? `em ${dataCurta(c?.verificado_em)}` : 'não pode ser usado em pagamentos'}
                    </Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>

              <TextField
                fullWidth size="small" multiline minRows={2} sx={{ mb: 2 }}
                label="Motivo (obrigatório para confirmar ou corrigir)"
                value={motivo} onChange={(e) => setMotivo(e.target.value)}
                helperText="Fica registado. Sem motivo escrito não se distingue uma correcção legítima de um favor."
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                <Button
                  variant="contained" startIcon={<Verified />}
                  disabled={jaConfirmado || !motivo.trim() || aGravar}
                  onClick={confirmar}
                >
                  Confirmar contribuinte
                </Button>
                <TextField
                  size="small" label="Novo número" value={novoNumero}
                  onChange={(e) => setNovoNumero(e.target.value)} sx={{ width: 180 }}
                />
                <Button
                  variant="outlined" startIcon={<Edit />}
                  disabled={!motivo.trim() || !novoNumero.trim() || novoNumero === c?.numero_social || aGravar}
                  onClick={corrigirNumero}
                >
                  Corrigir número
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Suportes</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>UID</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell align="right">Acção</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalhe.cartoes.map((k) => (
                      <TableRow key={k.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{k.nfc_uid}</TableCell>
                        <TableCell>
                          {k.suporte === 'PULSEIRA'
                            ? <Watch fontSize="small" sx={{ verticalAlign: 'middle' }} />
                            : <CreditCard fontSize="small" sx={{ verticalAlign: 'middle' }} />}
                        </TableCell>
                        <TableCell><Chip size="small" label={k.estado} color={corDoEstado(k.estado) as any} /></TableCell>
                        <TableCell><Typography variant="caption">{k.motivo || '—'}</Typography></TableCell>
                        <TableCell align="right">
                          {k.estado === 'ACTIVO' ? (
                            <Button size="small" color="error" onClick={() => mudarEstadoCartao(k.id, 'BLOQUEADO')}>
                              Bloquear
                            </Button>
                          ) : (
                            <Button size="small" onClick={() => mudarEstadoCartao(k.id, 'ACTIVO')}>
                              Reactivar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Últimos pagamentos</Typography>
              {detalhe.pagamentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Ainda não há pagamentos.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Referência</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Terminal</TableCell>
                        <TableCell>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalhe.pagamentos.map((pg) => (
                        <TableRow key={pg.id} hover>
                          <TableCell>{pg.referencia_divida}</TableCell>
                          <TableCell align="right">{Number(pg.valor_cve).toLocaleString('pt-PT')} CVE</TableCell>
                          <TableCell><Chip size="small" label={pg.estado} /></TableCell>
                          <TableCell>{pg.terminal_id || '—'}</TableCell>
                          <TableCell>{dataCurta(pg.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default INPSAdmin;
